import React, { useEffect, useMemo, useState } from 'react';
import {
  BuildingLibraryIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { ModalShell } from './modals/core';

export type PaymentNoticeType = 'reminder' | 'dunning' | 'final_dunning';

export interface PaymentNoticeSummary {
  member_id: number | string;
  notice_type: PaymentNoticeType;
  sent_at: string;
  sent_by?: number | null;
  sent_by_name?: string | null;
  notice_count: number;
}

interface ContributionRates {
  adults?: number | null;
  partner?: number | null;
  youth?: number | null;
}

interface PaymentNoticeModalProps {
  open: boolean;
  member: {
    mg_nr: number | string;
    mitglied_nr?: string;
    vorname: string;
    name: string;
    email?: string;
    geb_am?: string;
  };
  year: number;
  rates?: ContributionRates;
  summary?: PaymentNoticeSummary;
  onClose: () => void;
  onSent: (summary: PaymentNoticeSummary) => void;
}

const noticeOptions: Array<{
  type: PaymentNoticeType;
  label: string;
  short: string;
  description: string;
  tone: string;
}> = [
  {
    type: 'reminder',
    label: 'Zahlungserinnerung',
    short: 'Erinnerung',
    description: 'Freundlicher Hinweis ohne Mahncharakter.',
    tone: 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/25 dark:text-emerald-300',
  },
  {
    type: 'dunning',
    label: 'Mahnung',
    short: 'Mahnung',
    description: 'Verbindliche Zahlungsaufforderung nach einer Erinnerung.',
    tone: 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950/25 dark:text-amber-300',
  },
  {
    type: 'final_dunning',
    label: 'Letzte Mahnung',
    short: 'Letzte',
    description: 'Letzte vereinsinterne Frist nach Erinnerung und Mahnung.',
    tone: 'border-red-500 bg-red-50 text-red-800 dark:bg-red-950/25 dark:text-red-300',
  },
];

const formatDate = (value: string) => new Date(value).toLocaleDateString('de-DE');

const PaymentNoticeModal: React.FC<PaymentNoticeModalProps> = ({
  open,
  member,
  year,
  rates,
  summary,
  onClose,
  onSent,
}) => {
  const defaultRate = useMemo(() => {
    if (member.geb_am) {
      const birthDate = new Date(member.geb_am);
      const ageAtYearEnd = year - birthDate.getFullYear();
      if (!Number.isNaN(ageAtYearEnd) && ageAtYearEnd < 18 && Number(rates?.youth) > 0) return 'youth';
    }
    return Number(rates?.adults) > 0 ? 'adults' : Number(rates?.partner) > 0 ? 'partner' : 'custom';
  }, [member.geb_am, rates, year]);

  const [noticeType, setNoticeType] = useState<PaymentNoticeType>('reminder');
  const [rateType, setRateType] = useState(defaultRate);
  const [openAmount, setOpenAmount] = useState('');
  const [dogBoxIncluded, setDogBoxIncluded] = useState(false);
  const [originalDueDate, setOriginalDueDate] = useState('');
  const [paymentDeadline, setPaymentDeadline] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const nextType: PaymentNoticeType = summary?.notice_type === 'dunning' || summary?.notice_type === 'final_dunning'
      ? 'final_dunning'
      : summary ? 'dunning' : 'reminder';
    const nextDeadline = new Date();
    nextDeadline.setDate(nextDeadline.getDate() + 14);
    const initialRate = defaultRate;
    const rateValue = initialRate === 'custom' ? '' : String(Number(rates?.[initialRate as keyof ContributionRates] || 0));
    setNoticeType(nextType);
    setRateType(initialRate);
    setOpenAmount(rateValue);
    setDogBoxIncluded(false);
    setOriginalDueDate('');
    setPaymentDeadline(nextDeadline.toISOString().slice(0, 10));
    setError(null);
  }, [defaultRate, open, rates, summary]);

  const chooseRate = (value: string) => {
    setRateType(value);
    if (value !== 'custom') setOpenAmount(String(Number(rates?.[value as keyof ContributionRates] || 0)));
  };

  const send = async () => {
    const contributionAmount = Number(openAmount.replace(',', '.'));
    const dogBoxFee = dogBoxIncluded ? 10 : 0;
    const amount = contributionAmount + dogBoxFee;
    if (!Number.isFinite(contributionAmount) || contributionAmount <= 0) {
      setError('Bitte einen gültigen Grundbeitrag angeben.');
      return;
    }
    if (!paymentDeadline) {
      setError('Bitte eine konkrete Zahlungsfrist angeben.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      const response = await apiClient.post(`/api/members/payment-notices/${member.mg_nr}/send`, {
        contribution_year: year,
        notice_type: noticeType,
        open_amount: amount,
        dog_box_fee: dogBoxFee,
        original_due_date: originalDueDate || null,
        payment_deadline: paymentDeadline,
      });
      onSent({
        member_id: member.mg_nr,
        notice_type: noticeType,
        sent_at: response.data.notice?.sent_at || new Date().toISOString(),
        sent_by: response.data.notice?.sent_by,
        notice_count: Number(summary?.notice_count || 0) + 1,
      });
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Die E-Mail konnte nicht versendet werden.');
    } finally {
      setSending(false);
    }
  };

  const selectedOption = noticeOptions.find(option => option.type === noticeType)!;
  const contributionAmount = Number(openAmount.replace(',', '.')) || 0;
  const totalAmount = contributionAmount + (dogBoxIncluded ? 10 : 0);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="form"
      size="md"
      icon={EnvelopeIcon}
      accent={noticeType === 'final_dunning' ? 'danger' : 'brand'}
      title={selectedOption.label}
      subtitle={`${member.vorname} ${member.name} · Beitrag ${year}`}
      bodyClassName="space-y-5 bg-stone-50/70 dark:bg-gray-900/20"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={sending} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
            Abbrechen
          </button>
          <button type="button" onClick={send} disabled={sending || !member.email} className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${noticeType === 'final_dunning' ? 'bg-red-600 hover:bg-red-700' : noticeType === 'dunning' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-700 hover:bg-emerald-800'}`}>
            <PaperAirplaneIcon className="h-4 w-4" />
            {sending ? 'Wird versendet …' : `${selectedOption.label} senden`}
          </button>
        </div>
      }
    >
      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Versandstufe</p>
        <div className="grid grid-cols-3 gap-2">
          {noticeOptions.map(option => {
            const disabled = (option.type === 'dunning' && !summary)
              || (option.type === 'final_dunning'
                && summary?.notice_type !== 'dunning'
                && summary?.notice_type !== 'final_dunning');
            return (
              <button
                key={option.type}
                type="button"
                disabled={disabled}
                onClick={() => setNoticeType(option.type)}
                title={disabled ? 'Die vorherige Versandstufe fehlt.' : option.description}
                className={`rounded-xl border px-2 py-3 text-center text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${noticeType === option.type ? option.tone : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
              >
                {option.short}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{selectedOption.description}</p>
      </div>

      {summary && (
        <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-3 text-sky-900 dark:border-sky-900/70 dark:bg-sky-950/25 dark:text-sky-200">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none" />
          <div className="text-xs leading-5">
            Zuletzt wurde am <strong>{formatDate(summary.sent_at)}</strong> eine Versandstufe protokolliert. Insgesamt {summary.notice_count} {summary.notice_count === 1 ? 'Nachricht' : 'Nachrichten'}.
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Beitragssatz</span>
          <select value={rateType} onChange={event => chooseRate(event.target.value)} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            {Number(rates?.adults) > 0 && <option value="adults">Erwachsene · {Number(rates?.adults).toFixed(2)} €</option>}
            {Number(rates?.partner) > 0 && <option value="partner">Partner · {Number(rates?.partner).toFixed(2)} €</option>}
            {Number(rates?.youth) > 0 && <option value="youth">Jugend · {Number(rates?.youth).toFixed(2)} €</option>}
            <option value="custom">Anderer Betrag</option>
          </select>
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Grundbeitrag</span>
          <div className="relative"><input type="number" min="0.01" step="0.01" value={openAmount} onChange={event => { setOpenAmount(event.target.value); setRateType('custom'); }} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-9 text-sm text-gray-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /><span className="absolute right-3 top-2.5 text-sm text-gray-500">€</span></div>
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Ursprüngliche Fälligkeit <span className="font-normal text-gray-400">(optional)</span></span>
          <div className="relative"><CalendarDaysIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><input type="date" value={originalDueDate} onChange={event => setOriginalDueDate(event.target.value)} className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></div>
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Neue Zahlungsfrist</span>
          <div className="relative"><CalendarDaysIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><input type="date" value={paymentDeadline} onChange={event => setPaymentDeadline(event.target.value)} className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></div>
        </label>
      </div>

      <label className={`flex cursor-pointer items-center justify-between gap-4 rounded-xl border p-3.5 transition-colors ${dogBoxIncluded ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-800 dark:bg-emerald-950/25' : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'}`}>
        <span>
          <span className="block text-sm font-semibold text-gray-900 dark:text-white">Hundebox berücksichtigen</span>
          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">Zusätzliche Position von 10,00 €</span>
        </span>
        <span className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-900 dark:text-white">+ 10,00 €</span>
          <input type="checkbox" checked={dogBoxIncluded} onChange={event => setDogBoxIncluded(event.target.checked)} className="h-5 w-5 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600 dark:border-gray-600 dark:bg-gray-800" />
        </span>
      </label>

      <div className="flex items-center justify-between rounded-xl bg-gray-900 px-4 py-3 text-white dark:bg-emerald-950/70 dark:ring-1 dark:ring-emerald-800">
        <span className="text-sm font-medium text-gray-300">Offener Gesamtbetrag</span>
        <span className="text-lg font-bold tabular-nums">{totalAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Empfänger</p>
        <p className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-white">{member.email || 'Keine E-Mail-Adresse hinterlegt'}</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Verwendungszweck: Mitgliedsbeitrag {year} · {member.vorname} {member.name}</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 dark:border-amber-900/70 dark:bg-amber-950/20">
        <div className="flex items-start gap-3">
          <BuildingLibraryIcon className="mt-0.5 h-5 w-5 flex-none text-amber-700 dark:text-amber-400" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-800 dark:text-amber-300">Bankverbindung in der E-Mail</p>
            <p className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-white">HSV Berlin-Adlershof e.V.</p>
            <p className="mt-0.5 font-mono text-sm text-gray-800 dark:text-gray-200">DE93 1009 0000 7283 1450 04</p>
            <p className="mt-1.5 text-xs leading-5 text-gray-600 dark:text-gray-400">Der Empfängername soll auch bei einem abweichenden Namenshinweis der Bank verwendet werden.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800 dark:border-red-900/70 dark:bg-red-950/25 dark:text-red-300">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none" />
          <span>{error}</span>
        </div>
      )}
    </ModalShell>
  );
};

export default PaymentNoticeModal;
